"use client"

import NextLink from "next/link"
import { type ComponentProps, type MouseEvent, useState } from "react"

type LinkProps = ComponentProps<typeof NextLink>

const getTargetElement = (hash: string): HTMLElement | null => {
  const rawId = hash.slice(1)

  try {
    return document.getElementById(decodeURIComponent(rawId)) ?? document.getElementById(rawId)
  } catch {
    return document.getElementById(rawId)
  }
}

const scrollToSamePageHash = (href: LinkProps["href"], event: MouseEvent<HTMLAnchorElement>) => {
  if (
    typeof href !== "string" ||
    !href.includes("#") ||
    event.button !== 0 ||
    event.metaKey ||
    event.altKey ||
    event.ctrlKey ||
    event.shiftKey ||
    (event.currentTarget.target && event.currentTarget.target !== "_self")
  ) {
    return
  }

  let url: URL
  try {
    url = new URL(href, globalThis.location.href)
  } catch {
    return
  }

  if (
    url.origin !== globalThis.location.origin ||
    url.pathname !== globalThis.location.pathname ||
    url.search !== globalThis.location.search ||
    !url.hash
  ) {
    return
  }

  const target = getTargetElement(url.hash)
  if (!target) {
    return
  }

  event.preventDefault()
  target.scrollIntoView({ block: "start" })
  globalThis.history.pushState(null, "", `${url.pathname}${url.search}${url.hash}`)
}

/**
 * This component is used to create a link that prefetches the page when the user hovers over it.
 * If you're ok with the default behavior of NextLink, just use "next/link" instead.
 */
export const Link = ({ prefetch: nextPrefetch, onClick, href, ...props }: LinkProps) => {
  const [prefetch, setPrefetch] = useState(nextPrefetch ?? false)

  return (
    <NextLink
      href={href}
      prefetch={prefetch}
      onClick={event => {
        onClick?.(event)
        if (!event.defaultPrevented) {
          scrollToSamePageHash(href, event)
        }
      }}
      onMouseEnter={() => setPrefetch(true)}
      {...props}
    />
  )
}
